import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { ReactComponent as CheckIcon } from "../../../shared/assets/Icons/CheckIcon.svg";
import { ReactComponent as CrossIcon } from "../../../shared/assets/Icons/CrossIcon.svg";

/* ------------------- BGP ------------------- */
export interface Status {
	ctxr: string;
	ctxr_description: string;
	neighbor: string;
	description: string | null;
	state: string;
	up: string;
	local_as: number;
	peer_as: number;
	in: number;
	out: number;
}

export const statusColumns: GenericTableColumn<Status>[] = [
	{
		header: (
			<>
				CTX-R /
				<br />
				Description
			</>
		),
		renderCell: (row) => (
			<div>
				<div>{row.ctxr}</div>
				<div className="text-sm text-gray-500">{row.ctxr_description}</div>
			</div>
		),
	},
	{
		header: (
			<>
				Neighbor /
				<br />
				Description
			</>
		),
		renderCell: (row) => (
			<div>
				<div>{row.neighbor}</div>
				<div className="text-sm text-gray-500">{row.description ?? ""}</div>
			</div>
		),
	},
	{
		header: (
			<>
				Status /
				<br />
				Time
			</>
		),
		renderCell: (row) => (
			<div>
				<div
					className={`${
						row.state.toLowerCase() === "established"
							? "text-semantic-text-success"
							: "text-semantic-text-danger"
					}`}
				>
					{row.state}
				</div>
				<div className="text-sm text-gray-500">{row.up}</div>
			</div>
		),
	},
	{
		header: (
			<>
				Local AS /
				<br />
				Peer AS
			</>
		),
		renderCell: (row) => (
			<div>
				<div>{row.local_as}</div>
				<div className="text-sm text-gray-500">{row.peer_as}</div>
			</div>
		),
	},
	{
		header: (
			<>
				Prefix RcV /
				<br />
				Snt
			</>
		),
		renderCell: (row) => (
			<div>
				<div>{row.in}</div>
				<div className="text-sm text-gray-500">{row.out}</div>
			</div>
		),
	},
];

/* ------------------- CLOUD ------------------- */
export interface CloudHealth {
	cloud: string;
	region: string;
	status: string;
}

export const cloudColumns: GenericTableColumn<CloudHealth>[] = [
	{
		header: "Cloud",
		renderCell: (row) => row.cloud,
	},
	{
		header: "Region",
		renderCell: (row) => row.region,
	},
	{
		header: "Status",
		renderCell: (row) => {
			const isHealthy =
				row.status.toLowerCase() === "up" ||
				row.status.toLowerCase() === "healthy";
			return isHealthy ? (
				<CheckIcon className="w-5 h-5  text-semantic-text-success" />
			) : (
				<CrossIcon className="w-5 h-5 text-semantic-text-danger" />
			);
		},
	},
];

/* ------------------- INSTANCES ------------------- */
export interface InstanceMetadata {
	instance_id?: string;
	instance_type?: string;
	cloud_hostname?: string;
	local_hostname?: string;
	availability_zone?: string;
	security_groups?: string;
}

export interface InstanceData {
	name: string;
	description: string;
	region: string;
	status: string;
	version: string | null;
	metadata: InstanceMetadata;
	onViewDetails?: (instance: InstanceData) => void;
}

export const instancesColumns: GenericTableColumn<InstanceData>[] = [
	{
		header: "CTX-R Name",
		renderCell: (row) => row.name,
	},
	{
		header: "Description",
		renderCell: (row) => row.description,
	},
	{
		header: "Region",
		renderCell: (row) => row.region,
	},
	{
		header: "Status",
		renderCell: (row) => {
			const isHealthy =
				row.status.toLowerCase() === "up" ||
				row.status.toLowerCase() === "healthy";
			return isHealthy ? (
				<CheckIcon className="w-5 h-5 text-semantic-text-success" />
			) : (
				<CrossIcon className="w-5 h-5 text-semantic-text-danger" />
			);
		},
	},
	{
		header: "Version",
		renderCell: (row) => {
			const version = row.version ?? "?";
			const isDisabled = !version || version === "?";

			if (isDisabled) {
				return (
					<GenericTooltip text="Contact CTX support" position="top">
						<button
							disabled
							className="text-semantic-text-secondary cursor-not-allowed"
						>
							{version}
						</button>
					</GenericTooltip>
				);
			}

			return (
				<button
					onClick={() => row.onViewDetails?.(row)}
					className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
				>
					{version}
				</button>
			);
		},
	},
];

/* ------------------- HA ------------------- */
export interface HaData {
	clusterName: string;
	description: string;
	status: string;
}

export const haColumns: GenericTableColumn<HaData>[] = [
	{
		header: "Cluster Name",
		renderCell: (row) => row.clusterName,
	},
	{
		header: "Description",
		renderCell: (row) => row.description,
	},
	{
		header: "Status",
		renderCell: (row) => {
			const isEnabled = row.status.toLowerCase() === "enabled";
			return isEnabled ? (
				<CheckIcon className="w-5 h-5 text-semantic-text-success" />
			) : (
				<CrossIcon className="w-5 h-5 text-semantic-text-danger" />
			);
		},
	},
];
